import { c as _c } from "react/compiler-runtime";
import { IconButton, Stack, TablePagination, Tooltip, Typography, type IconButtonProps } from '@mui/material';
import React, { forwardRef } from 'react';
import { TableSettings, type TableSettingsSource } from '../table/tableSettings';
const DEFAULT_ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

// MUI's `count` for a total the server hasn't given us (cursor pagination).
export const UNKNOWN_RECORDS_COUNT = -1;

// What a toolbar needs to run its settings panel.
export interface PageSizeSource {
  pageSize: number;
  rowsPerPageOptions?: number[];
  // Receives the page to land on, already corrected for the new size.
  onPageSizeChange: (pageSize: number, pageIndex: number) => void;
}
const formatRangeLabel = ({
  from,
  to,
  count,
  pageIndex,
  recordsLimit,
  totalLabel,
  rangeEnd
}: {
  from: number;
  to: number;
  count: number;
  pageIndex: number;
  recordsLimit?: number;
  totalLabel?: string;
  rangeEnd?: number;
}) => {
  if (count !== UNKNOWN_RECORDS_COUNT) {
    const total = totalLabel ?? (count === recordsLimit ? `more than ${count.toLocaleString()}` : count.toLocaleString());
    return `${from}–${to} of ${total}`;
  }
  if (rangeEnd === undefined) {
    return `Page ${pageIndex + 1}`;
  }
  return `${from}–${rangeEnd.toLocaleString()} of more than ${rangeEnd.toLocaleString()}`;
};
export const PageNavigation = t0 => {
  const $ = _c(27);
  const {
    pageIndex,
    pageSize,
    recordsCount,
    recordsLimit,
    reachedLastPage,
    totalLabel,
    rangeEnd,
    isLoading,
    onPageChange,
    labelPrefix,
    minHeight
  } = t0;
  let t1;
  if ($[0] !== minHeight) {
    t1 = {
      "& .MuiToolbar-root": {
        padding: "0 !important",
        minHeight
      }
    };
    $[0] = minHeight;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== pageSize) {
    t2 = [pageSize];
    $[2] = pageSize;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onPageChange) {
    t3 = (_, page) => onPageChange(page);
    $[4] = onPageChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = IconButtonPropsFactory("Previous page");
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== reachedLastPage) {
    t5 = IconButtonPropsFactory("Next page", reachedLastPage);
    $[7] = reachedLastPage;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = {
      actions: {
        previousButton: t4,
        nextButton: t5
      }
    };
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== labelPrefix || $[12] !== pageIndex || $[13] !== rangeEnd || $[14] !== recordsLimit || $[15] !== totalLabel) {
    t7 = t8 => {
      const {
        from,
        to,
        count
      } = t8;
      return <Stack component="span" direction="row" sx={{
        alignItems: "center"
      }}>{labelPrefix}<Typography component="span">{formatRangeLabel({
            from,
            to,
            count,
            pageIndex,
            recordsLimit,
            totalLabel,
            rangeEnd
          })}</Typography></Stack>;
    };
    $[11] = labelPrefix;
    $[12] = pageIndex;
    $[13] = rangeEnd;
    $[14] = recordsLimit;
    $[15] = totalLabel;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== isLoading || $[18] !== pageIndex || $[19] !== pageSize || $[20] !== recordsCount || $[21] !== t1 || $[22] !== t2 || $[23] !== t3 || $[24] !== t6 || $[25] !== t7) {
    t8 = <TablePagination sx={t1} data-testid="pagination-bar" rowsPerPageOptions={t2} rowsPerPage={pageSize} component="div" disabled={isLoading} count={recordsCount} page={pageIndex} onPageChange={t3} slotProps={t6} labelDisplayedRows={t7} labelRowsPerPage="" />;
    $[17] = isLoading;
    $[18] = pageIndex;
    $[19] = pageSize;
    $[20] = recordsCount;
    $[21] = t1;
    $[22] = t2;
    $[23] = t3;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
};

// A table pinned to a single page size has nothing to settle in the panel, unless it brought settings of its own.
export const canShowPageSizeSettings = (settings: TableSettingsSource, pageSizeControl: PageSizeSource | undefined): boolean => !!pageSizeControl && ((pageSizeControl.rowsPerPageOptions ?? DEFAULT_ROWS_PER_PAGE_OPTIONS).length > 1 || !!settings.content);
export const PageSizeSettings = t0 => {
  const $ = _c(9);
  const {
    settings,
    pageSizeControl,
    pageIndex,
    recordsCount
  } = t0;
  const t1 = pageSizeControl.rowsPerPageOptions ?? DEFAULT_ROWS_PER_PAGE_OPTIONS;
  let t2;
  if ($[0] !== pageIndex || $[1] !== pageSizeControl || $[2] !== recordsCount) {
    t2 = pageSize => pageSizeControl.onPageSizeChange(pageSize, clampPageIndex(pageSize, pageIndex, recordsCount));
    $[0] = pageIndex;
    $[1] = pageSizeControl;
    $[2] = recordsCount;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== pageSizeControl.pageSize || $[5] !== settings || $[6] !== t1 || $[7] !== t2) {
    t3 = <TableSettings settings={settings} pageSize={pageSizeControl.pageSize} rowsPerPageOptions={t1} onPageSizeChange={t2} />;
    $[4] = pageSizeControl.pageSize;
    $[5] = settings;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};

// Keeps the current page in range when a larger page size shrinks the page count.
const clampPageIndex = (pageSize: number, pageIndex: number, recordsCount: number) => {
  if (recordsCount < 0) {
    return pageIndex;
  }
  const lastPageIndex = Math.max(Math.ceil(recordsCount / pageSize) - 1, 0);
  return Math.max(Math.min(pageIndex, lastPageIndex), 0);
};
const IconButtonPropsFactory = (tooltip: string, disabled?: boolean) => {
  const Button = forwardRef<HTMLButtonElement>((t0, ref) => {
    const $ = _c(6);
    let props;
    if ($[0] !== t0) {
      const {
        title,
        ...t1
      } = t0;
      props = t1;
      $[0] = t0;
      $[1] = props;
    } else {
      props = $[1];
    }
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = tooltip.toLowerCase();
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== props || $[4] !== ref) {
      t2 = <Tooltip title={tooltip}><span><IconButton {...props} disabled={disabled} ref={ref} data-testid={`${t1}-button`} disableRipple={true} /></span></Tooltip>;
      $[3] = props;
      $[4] = ref;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  });
  return {
    component: Button,
    disableRipple: true,
    sx: {
      borderRadius: '4px',
      padding: '6px',
      '&:hover': {
        backgroundColor: 'var(--mui-palette-background-primary)'
      },
      svg: {
        width: '20px',
        height: '20px'
      }
    }
  } satisfies IconButtonProps;
};